import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import useToast from '../hooks/useToast';
import ToastContainer from '../components/ToastContainer';

const BasvurularPage = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [filters, setFilters] = useState({
    type: '',
    status: '',
    department: '',
    date: ''
  });
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingApplication, setRejectingApplication] = useState(null);
  const { toasts, addToast, removeToast } = useToast();

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const [leaveRequests, expenseReports] = await Promise.all([
        api.getLeaveRequests(),
        api.getExpenseReports()
      ]);

      const allApplications = [
        ...leaveRequests.data.leaveRequests.map(req => ({
          id: req._id,
          employee: `${req.employee.firstName} ${req.employee.lastName}`,
          type: req.leaveType,
          date: new Date(req.createdAt).toLocaleDateString('tr-TR'),
          status: req.status,
          department: req.department,
          category: 'leave',
          reason: req.reason,
          startDate: req.startDate,
          endDate: req.endDate,
          createdAt: req.createdAt
        })),
        ...expenseReports.data.expenseReports.map(rep => ({
          id: rep._id,
          employee: `${rep.employee.firstName} ${rep.employee.lastName}`,
          type: 'Masraf Bildirimi',
          date: new Date(rep.createdAt).toLocaleDateString('tr-TR'),
          status: rep.status,
          department: rep.department,
          category: 'expense',
          amount: rep.amount,
          description: rep.description,
          createdAt: rep.createdAt
        }))
      ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setApplications(allApplications);
    } catch (error) {
      console.error('Başvurular yüklenirken hata:', error);
      addToast(
        'Başvurular yüklenirken bir hata oluştu.',
        'error',
        5000
      );
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filteredApplications = applications.filter(app => {
    if (filters.type && app.category !== filters.type) return false;
    if (filters.status && app.status !== filters.status) return false;
    if (filters.department && app.department !== filters.department) return false;
    return true;
  });

  const handleApprove = async (application) => {
    try {
      const approverId = localStorage.getItem('userId');
      await api.approveApplication(application.id, application.category, approverId);
      
      addToast(
        `${application.employee} adlı çalışanın başvurusu başarıyla onaylandı.`,
        'success',
        5000
      );
      
      fetchApplications();
    } catch (error) {
      console.error('Başvuru onaylanırken hata:', error);
      addToast(
        'Başvuru onaylanırken bir hata oluştu. Lütfen tekrar deneyin.',
        'error',
        5000
      );
    }
  };

  const handleReject = (application) => {
    setRejectingApplication(application);
    setShowRejectModal(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingApplication || !rejectReason.trim()) {
      addToast(
        'Lütfen red sebebini giriniz.',
        'warning',
        3000
      );
      return;
    }

    try {
      const approverId = localStorage.getItem('userId');
      await api.rejectApplication(rejectingApplication.id, rejectingApplication.category, approverId, rejectReason);
      
      addToast(
        `${rejectingApplication.employee} adlı çalışanın başvurusu reddedildi.`,
        'warning',
        5000
      );
      
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingApplication(null);
      fetchApplications();
    } catch (error) {
      console.error('Başvuru reddedilirken hata:', error);
      addToast(
        'Başvuru reddedilirken bir hata oluştu. Lütfen tekrar deneyin.',
        'error',
        5000
      );
    }
  };

  const handleCancelReject = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setRejectingApplication(null);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Beklemede': 'pending',
      'Onaylandı': 'approved',
      'Reddedildi': 'rejected'
    };
    return statusColors[status] || 'pending';
  };

  if (loading) {
    return (
      <div className="modern-hr-page">
        <div className="container-fluid">
          <div className="modern-hr-header">
            <h1>Başvurular</h1>
            <p>Tüm izin talepleri ve masraf raporları</p>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
              <h5 className="text-muted">Başvurular yükleniyor...</h5>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-hr-page">
      <div className="container-fluid">
        <div className="modern-hr-header">
          <h1>Başvurular</h1>
          <p>Tüm izin talepleri ve masraf raporları</p>
        </div>

        {/* Filtreler */}
        <div className="modern-hr-filters">
          <div className="row">
            <div className="col-md-3">
              <div className="modern-hr-filter-group">
                <label className="modern-hr-filter-label">Başvuru Türü</label>
                <select 
                  className="modern-hr-filter-select"
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <option value="">Tümü</option>
                  <option value="leave">İzin Talepleri</option>
                  <option value="expense">Masraf Raporları</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="modern-hr-filter-group">
                <label className="modern-hr-filter-label">Durum</label>
                <select 
                  className="modern-hr-filter-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">Tümü</option>
                  <option value="Beklemede">Beklemede</option>
                  <option value="Onaylandı">Onaylandı</option>
                  <option value="Reddedildi">Reddedildi</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="modern-hr-filter-group">
                <label className="modern-hr-filter-label">Departman</label>
                <select 
                  className="modern-hr-filter-select"
                  value={filters.department}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                >
                  <option value="">Tümü</option>
                  <option value="Saha Mühendisliği">Saha Mühendisliği</option>
                  <option value="Muhasebe">Muhasebe</option>
                  <option value="İnsan Kaynakları">İnsan Kaynakları</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <div className="modern-hr-filter-group">
                <label className="modern-hr-filter-label">Tarih</label>
                <input 
                  type="date" 
                  className="modern-hr-filter-input"
                  value={filters.date}
                  onChange={(e) => handleFilterChange('date', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Başvuru Listesi */}
        <div className="modern-hr-applications">
          <div className="modern-hr-applications-header">
            <div className="modern-hr-applications-title">
              <h5>Başvuru Listesi</h5>
              <span className="modern-hr-applications-count">Toplam: {filteredApplications.length} başvuru</span>
            </div>
            <div className="modern-hr-applications-actions">
              <button className="modern-hr-action-btn" onClick={fetchApplications}>
                <i className="bx bx-refresh"></i>
                Yenile
              </button>
            </div>
          </div>

          <div className="modern-hr-applications-list">
            {filteredApplications.length > 0 ? (
              filteredApplications.map((application) => (
                <div key={application.id} className="modern-hr-application-card">
                  <div className="modern-hr-application-header">
                    <div className="modern-hr-application-info">
                      <div className="modern-hr-application-avatar">
                        <i className="bx bx-user"></i>
                      </div>
                      <div className="modern-hr-application-details">
                        <h6>{application.employee}</h6>
                        <p>{application.department}</p>
                      </div>
                    </div>
                    <div className="modern-hr-application-status">
                      <span className={`modern-hr-badge ${getStatusBadge(application.status)}`}>
                        {application.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="modern-hr-application-body">
                    <div className="modern-hr-application-type">
                      <i className={application.category === 'leave' ? 'bx bx-calendar' : 'bx bx-receipt'}></i>
                      <span>{application.type}</span>
                    </div>
                    <div className="modern-hr-application-date">
                      <i className="bx bx-time"></i>
                      <span>
                        {application.category === 'leave' 
                          ? `${application.startDate} - ${application.endDate}`
                          : application.date
                        }
                      </span>
                    </div>
                    <div className="modern-hr-application-reason">
                      <p>
                        {application.category === 'leave' 
                          ? application.reason
                          : application.description
                        }
                        {application.amount && ` - ₺${application.amount}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="modern-hr-application-footer">
                    <div className="modern-hr-application-actions">
                      {application.status === 'Beklemede' && (
                        <>
                          <button 
                            className="modern-hr-action-btn success" 
                            onClick={() => handleApprove(application)}
                          >
                            <i className="bx bx-check"></i>
                            Onayla
                          </button>
                          <button 
                            className="modern-hr-action-btn danger" 
                            onClick={() => handleReject(application)}
                          >
                            <i className="bx bx-x"></i>
                            Reddet
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="modern-hr-empty-state">
                <i className="bx bx-inbox"></i>
                <h5>Başvuru bulunamadı</h5>
                <p>Filtre kriterlerinize uygun başvuru bulunmuyor.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modern-modal-overlay" onClick={handleCancelReject}>
          <div className="modern-reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modern-reject-header">
              <div className="modern-reject-title">
                <i className="bx bx-x-circle"></i>
                <h4>Başvuruyu Reddet</h4>
              </div>
              <button className="modern-reject-close" onClick={handleCancelReject}>
                <i className="bx bx-x"></i>
              </button>
            </div>
            
            <div className="modern-reject-body">
              <div className="modern-reject-info">
                <p><strong>Çalışan:</strong> {rejectingApplication?.employee}</p>
                <p><strong>Başvuru Türü:</strong> {rejectingApplication?.type}</p>
                <p><strong>Departman:</strong> {rejectingApplication?.department}</p>
              </div>
              
              <div className="modern-reject-form">
                <label htmlFor="rejectReason" className="modern-reject-label">
                  Red Sebebi <span className="text-danger">*</span>
                </label>
                <textarea
                  id="rejectReason"
                  className="modern-reject-textarea"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Red sebebini detaylı olarak açıklayınız..."
                  rows={4}
                />
                <small className="modern-reject-help">
                  Red sebebi çalışana bildirilecektir. Lütfen açıklayıcı bir sebep giriniz.
                </small>
              </div>
            </div>
            
            <div className="modern-reject-footer">
              <button className="btn btn-outline-secondary" onClick={handleCancelReject}>
                <i className="bx bx-x me-2"></i>İptal
              </button>
              <button className="btn btn-danger" onClick={handleConfirmReject}>
                <i className="bx bx-x me-2"></i>Reddet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default BasvurularPage;
